import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { en } from './catalogues/en';
import { ptBR } from './catalogues/pt-BR';
import {
  DEFAULT_LOCALE,
  applyLocaleToDocument,
  clearActiveLocale,
  detectLocale,
  readStoredLocale,
  setActiveLocale,
  storeLocale,
  type Locale,
} from './locale';

/** A flat key → text map. One per locale, per application. */
export type Catalogue = Readonly<Record<string, string>>;

/** The application's catalogues. The design system's own are merged underneath. */
export type Catalogues = Readonly<Record<Locale, Catalogue>>;

/** Values substituted into `{placeholders}`. `count` additionally selects a plural. */
export type TranslationParams = Readonly<Record<string, string | number>>;

export type Translate = (key: string, params?: TranslationParams) => string;

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: Translate;
}

/* -----------------------------------------------------------------------------
   Resolution
   -------------------------------------------------------------------------- */

/**
 * Plural forms, cached per locale.
 *
 * `Intl.PluralRules` is the platform's answer, and the two languages do NOT
 * share the same categories — measured on Node 22, `en` resolves to
 * `one | other` while `pt-BR` resolves to `one | many | other`. A catalogue
 * therefore names a form rather than counting on there being two, and the
 * lookup below falls through the forms that exist.
 */
const pluralRules = new Map<string, Intl.PluralRules>();

function pluralFor(locale: string): Intl.PluralRules {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules;
}

/**
 * Builds the `t` for one locale.
 *
 * A count-carrying lookup tries `<key>.<category>` first and falls back to
 * `<key>` itself, so a string that has no plural to make — "No unread
 * notifications." beside "3 unread notifications." — is one key rather than a
 * pair with an empty half.
 *
 * A missing key returns the KEY, not an empty string. An interface that reads
 * `nav.schedule` is a visible defect with an obvious cause; an interface with a
 * hole in it is the same defect reported as a layout bug.
 */
function createTranslate(catalogue: Catalogue, locale: Locale): Translate {
  const rules = pluralFor(locale);

  return (key, params) => {
    const count = typeof params?.count === 'number' ? params.count : null;
    const fromCount = count === null ? undefined : pluralForm(catalogue, key, count, rules);
    const template = fromCount ?? catalogue[key] ?? key;

    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
      params[name] === undefined ? placeholder : String(params[name]),
    );
  };
}

/**
 * The form a count selects, or `undefined` when the string has no plural at all.
 *
 * Three rungs, each one a case that was executed rather than imagined:
 *
 *   - a count of ZERO takes `other`. CLDR classifies 0 as `one` in pt-BR, which
 *     the locale is right about and the interface is not: that rule is there for
 *     0,5, and a reader shown "0 empreendimento" reads a mistake where "0
 *     empreendimentos" is what anyone would say. English already classifies 0 as
 *     `other`, so this makes both languages agree with the people reading them.
 *   - otherwise the locale's own category — `many`, for pt-BR, is what a million
 *     selects.
 *   - then `other`, which every countable string here defines. This is the rung
 *     that stops a category no catalogue has authored from falling past the
 *     entry and rendering a raw key.
 *   - `undefined` lets the caller fall back to a plain, uncounted string, which
 *     is what a key with no plural pair is.
 */
function pluralForm(
  catalogue: Catalogue,
  key: string,
  count: number,
  rules: Intl.PluralRules,
): string | undefined {
  const form = count === 0 ? 'other' : rules.select(count);
  return catalogue[`${key}.${form}`] ?? catalogue[`${key}.other`];
}

/**
 * The design system's base strings under the application's own.
 *
 * Merged here rather than at the call site so a consumer passes only the
 * catalogue it owns and can never omit the library's half — the failure that
 * would hide is a design-system string rendering as its own key, on one screen,
 * in one language.
 */
function mergeCatalogue(catalogue: Catalogues): Record<Locale, Catalogue> {
  return {
    en: { ...en, ...catalogue.en },
    'pt-BR': { ...ptBR, ...catalogue['pt-BR'] },
  };
}

/* -----------------------------------------------------------------------------
   Context
   -------------------------------------------------------------------------- */

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * What `useTranslation` answers with when no provider is above the caller.
 *
 * The design system must render on its own — its stories, its showcase and any
 * consumer that has not adopted the provider all mount components directly — so
 * the fallback is the library's English rather than an exception. `setLocale` is
 * a no-op for the honest reason: there is no state to change, and a control that
 * appeared to switch the language while nothing re-rendered would be a lie.
 *
 * Built once at module scope, so a hook called on every render returns the same
 * object and a dependency list holding `t` stays stable.
 */
const fallbackBase: Catalogue = en;
const FALLBACK: LocaleContextValue = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: createTranslate(fallbackBase, DEFAULT_LOCALE),
};

export interface LocaleProviderProps {
  /** The application's own strings. The design system's are merged underneath. */
  catalogue: Catalogues;
  children: ReactNode;
}

/**
 * Holds the interface language for the tree below it.
 *
 * The initial locale is the reader's stored choice, and only when there is none
 * is the browser asked. That order is the whole requirement: a preference the
 * reader expressed outranks one their operating system expressed, and it has to
 * be read during the first render rather than corrected afterwards, or every
 * page load would paint the wrong language first.
 *
 * The provider also answers the two questions the rest of the interface asks
 * about a locale: `documentElement.lang`, which is what tells a screen reader
 * and a search engine which language the page is in, and the tag the value
 * formatters resolve to (see `setActiveLocale`).
 */
export function LocaleProvider({ catalogue, children }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(
    () => readStoredLocale() ?? detectLocale(),
  );

  const merged = useMemo(() => mergeCatalogue(catalogue), [catalogue]);

  /*
     Both of these are written during render, not in an effect: formatting runs
     in components' own bodies and `<html lang>` is read by the first painted
     frame, so a value written from an effect would arrive a paint late — the
     `pt-BR` reader's first screen formatted as `$ 2,000.00` and read aloud with
     English phonetics. See `locale.ts` for why the write is safe here.
  */
  setActiveLocale(locale);
  applyLocaleToDocument(locale);

  /*
     Re-asserted in an effect because StrictMode unmounts and remounts the tree
     once in development: the cleanup below clears the tag, and without this the
     clear would be the last write until the next render. The pair is idempotent,
     so the double invocation settles on the same value.
  */
  useEffect(() => {
    setActiveLocale(locale);
    return () => clearActiveLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    storeLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t: createTranslate(merged[locale], locale) }),
    [locale, setLocale, merged],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** The active locale and the way to change it. */
export function useLocale(): { locale: Locale; setLocale: (next: Locale) => void } {
  const value = useContext(LocaleContext) ?? FALLBACK;
  return { locale: value.locale, setLocale: value.setLocale };
}

/**
 * The translator, plus the locale that produced it.
 *
 * `t` is typed with a plain `string` key here, and the application narrows it to
 * its own key union in one place. That is deliberate: the catalogue's shape
 * belongs to the consumer, and the design system cannot name a key it has never
 * seen. A narrowed alias assigns without a cast — a function taking any string
 * satisfies a signature taking fewer — so the application gets a compile-time
 * check on every key it writes without the library inventing a generics
 * parameter to carry it.
 */
export function useTranslation(): LocaleContextValue {
  return useContext(LocaleContext) ?? FALLBACK;
}
